<script setup lang="ts">
import type { RemoteDatatableColumn } from './RemoteDatatable.vue';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { useSolicitarEncerramentoLegalAi2 } from '~/composables/useSolicitarEncerramentoLegalAi2';

interface Props {
  pastaCodigos?: string[];
  compromissoCodigos?: string[];
  compromisso?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const formRef = ref<any>();
const mostrarHistoricoSolicitacoes = ref<any>('h');

const colunas = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
] as RemoteDatatableColumn[];

const model = reactive({
  descricao: '',
});

const { status, result, errorMessage, submit, stopPolling, setLoading } =
  useSolicitarEncerramentoLegalAi2();

const isLoading = computed(() => status.value === 'loading');
const isSuccess = computed(() => status.value === 'success');
const isPartial = computed(() => status.value === 'partial');
const showForm = computed(() => !isSuccess.value && !isPartial.value);

const isLote = computed(() => {
  const totalPastas = props.pastaCodigos?.length ?? 0;
  const totalCompromissos = props.compromissoCodigos?.length ?? 0;
  return totalPastas > 1 || totalCompromissos > 1;
});

async function handleSubmit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  setLoading();

  const compromissoCodigos = props.compromissoCodigos?.length
    ? props.compromissoCodigos
    : props.compromisso
      ? [props.compromisso.codigo]
      : [];

  await submit({
    pastaCodigos: props.pastaCodigos ?? [],
    compromissoCodigos,
    descricao: model.descricao,
  });

  if (status.value === 'error') {
    notification.error({
      message: errorMessage.value || 'Tivemos um problema inesperado',
      duration: 10,
    });
  }
}

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div>
    <a-collapse
      v-if="showForm && props.pastaCodigos?.length === 1"
      v-model:active-key="mostrarHistoricoSolicitacoes"
    >
      <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
        <CompromissoRemoteDatatable
          :columns="colunas"
          :show-dates-filter="false"
          :show-reload-button="false"
          :show-status-search-bar="false"
          :show-filter="false"
          :where-tipo-eq="350"
          :where-fluxo-pasta-codigo-in="props.pastaCodigos"
          order-data-inicio="desc"
        />
      </a-collapse-panel>
    </a-collapse>

    <div style="margin-top: 16px" />

    <a-form
      v-if="showForm"
      ref="formRef"
      :model="model"
      autocomplete="off"
      layout="vertical"
    >
      <a-alert
        v-if="
          (props.pastaCodigos && props.pastaCodigos.length > 1) ||
          (props.compromissoCodigos && props.compromissoCodigos.length > 1)
        "
        type="warning"
        message="Você irá realizar uma alteração em lote"
        show-icon
        style="margin-bottom: 24px"
      />

      <a-form-item
        label="Descrição"
        name="descricao"
        :rules="[{ message: 'Descrição é obrigatória', required: true }]"
      >
        <a-textarea
          v-model:value="model.descricao"
          :rows="4"
          placeholder="Explique o motivo da solicitação de encerramento"
        />
      </a-form-item>

      <a-form-item>
        <a-button
          type="primary"
          :disabled="isLoading"
          :loading="isLoading"
          size="large"
          @click.prevent="handleSubmit"
        >
          <template #icon>
            <SaveOutlined />
          </template>
          {{ isLoading ? 'Salvando...' : 'Salvar' }}
        </a-button>
      </a-form-item>
    </a-form>

    <!-- Resultado de sucesso (simples) -->
    <a-result
      v-if="isSuccess && !isLote"
      status="success"
      title="Solicitação realizada com sucesso"
      sub-title="Agora você pode fechar essa janela"
    >
      <template #extra>
        <a-button @click="() => emit('close')">Fechar</a-button>
      </template>
    </a-result>

    <!-- Resultado de sucesso (lote) -->
    <a-result
      v-if="isSuccess && isLote"
      status="success"
      title="Solicitações realizadas com sucesso"
      :sub-title="`${result?.totalSucesso || 0} solicitação(ões) processada(s) com sucesso`"
    >
      <template #extra>
        <a-list
          size="small"
          :data-source="result?.resultados || []"
          bordered
          style="margin-bottom: 16px; max-height: 300px; overflow-y: auto"
        >
          <template #renderItem="{ item }">
            <a-list-item
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
              "
            >
              <div style="text-align: left">
                <a :href="`/pasta/${item.codigo}`" target="_blank">
                  {{ item.nome || item.codigo }}
                </a>
              </div>
              <CheckCircleOutlined style="color: #52c41a; margin-left: 8px" />
            </a-list-item>
          </template>
        </a-list>
        <a-button @click="() => emit('close')">Fechar</a-button>
      </template>
    </a-result>

    <!-- Resultado parcial (lote com falhas) -->
    <a-result
      v-if="isPartial"
      status="warning"
      title="Processamento parcial"
      :sub-title="`${result?.totalSucesso || 0} sucesso(s), ${result?.totalFalha || 0} falha(s)`"
    >
      <template #extra>
        <a-list
          size="small"
          :data-source="result?.resultados || []"
          bordered
          style="margin-bottom: 16px; max-height: 450px; overflow-y: auto"
        >
          <template #renderItem="{ item }">
            <a-list-item
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
              "
            >
              <div style="text-align: left; flex: 1">
                <a :href="`/pasta/${item.codigo}`" target="_blank">
                  {{ item.nome || item.codigo }}
                </a>
                <span
                  v-if="!item.sucesso"
                  style="color: #ff4d4f; margin-left: 8px; font-size: 12px"
                >
                  - {{ item.erro }}
                </span>
              </div>
              <CheckCircleOutlined
                v-if="item.sucesso"
                style="color: #52c41a; margin-left: 8px"
              />
              <ExclamationCircleOutlined
                v-else
                style="color: #ff4d4f; margin-left: 8px"
              />
            </a-list-item>
          </template>
        </a-list>
        <a-button @click="() => emit('close')">Fechar</a-button>
      </template>
    </a-result>
  </div>
</template>
